import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tournamentsApi } from "../../api/tournaments";
import { tournamentKeys } from "../queries/useTournament";

export const useCreateTournament = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: tournamentsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tournamentKeys.all });
    },
  });
};

function useTournamentMutation<TVariables, TData = unknown>(
  mutationFn: (input: TVariables) => Promise<TData>,
  getTournamentId: (input: TVariables) => number | string = (input) => input as unknown as number,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (_data: TData, input: TVariables) => {
      const tournamentId = getTournamentId(input);
      queryClient.invalidateQueries({ queryKey: tournamentKeys.detail(tournamentId) });
    },
  });
}

export const useSetTournamentPlayers = () =>
  useTournamentMutation(tournamentsApi.setPlayers, (input) => input.tournamentId);

export const useStartTournament = () =>
  useTournamentMutation(tournamentsApi.start, (input) => input.tournamentId);

export const useSetPlayerSecondRoundGroup = () =>
  useTournamentMutation(tournamentsApi.setPlayerSecondRoundGroup, (input) => input.tournamentId);

function useBlastQueueMutation<TVariables extends { tournamentId: number }, TData = unknown>(
  mutationFn: (input: TVariables) => Promise<TData>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (_data: TData, { tournamentId }: TVariables) => {
      queryClient.invalidateQueries({ queryKey: tournamentKeys.detail(tournamentId) });
      queryClient.invalidateQueries({ queryKey: tournamentKeys.blastQueue(tournamentId) });
    },
  });
}

export const useBlastBenchPlayer = () => useBlastQueueMutation(tournamentsApi.blastBenchPlayer);

export const useBlastReturnPlayer = () => useBlastQueueMutation(tournamentsApi.blastReturnPlayer);

export const useFinishRound = () => useTournamentMutation(tournamentsApi.finishRound);

export const useCancelRound = () => useTournamentMutation(tournamentsApi.cancelRound);

export const useFinishTournament = () => useTournamentMutation(tournamentsApi.finishTournament);

export const useSetDoubleElimination = () =>
  useTournamentMutation(tournamentsApi.setDoubleElimination, (input) => input.tournamentId);

export const useToggleBlastGeneration = () =>
  useTournamentMutation(tournamentsApi.toggleBlastGeneration, (input) => input.tournamentId);
